import { IFutureOrder, ISupplierExposure } from '@sp-types/FutureOrderType';
import React, { useEffect, useMemo, useState } from 'react';
import { CellProps, Column, useExpanded, useFilters, useTable } from 'react-table';
import { useMaterialsStore } from '../../../../components/Stores/MaterialsStore';
import { useAccountStore } from '../../../../components/Stores/accountStore';
import { getDatesRangeAsString } from '../../../../utils/MonthYear/monthYear';
import { FormatToShortPriceTemplate, formatToShortString } from '../../../../utils/FormatValue';
import { FutureOrderIdLink } from './FutureOrderIdLink';
import { Delete, Edit } from '@mui/icons-material';
import { Button } from '@mui/material';
import { modifyCompletedOrder } from '../../AddNewFutureOrder/AddNewFutureOrderUtils';
import { useNavigate } from 'react-router-dom';
import { useFutureOrderStore } from '../../../../components/Stores/FutureOrdersStore';
import { ConfirmDelete } from '../../../../components/UI-Elements/FileUpload/ErrorComponents/ConfirmDelete';
import { deleteFutureOrder } from '../../../../sp-firebase/futureOrderFB';
import { getUnitObj } from '../../../../utils/MassUnits/massUnitsUtils';

interface FutureOrdersByMaterial {
  materialData: ISupplierExposure;
  orderId: string;
  orderProductQuantity: number;
  orderUuid?: string;
  futureOrderType?: 'BackToBack' | 'Indexed';
  order: IFutureOrder;
}

const mapFutureOrdersByMaterial = (
  futureOrders: IFutureOrder[],
  materialIds?: string[],
): { data: FutureOrdersByMaterial[]; materialId: string }[] => {
  const materialMap = futureOrders.reduce((acc, order) => {
    // wrong code... too complex
    order.supplierExposure.forEach((materialData) => {
      if (!materialIds || materialIds.includes(materialData.materialId)) {
        const futureOrder: FutureOrdersByMaterial = {
          materialData,
          orderId: order.orderId,
          orderUuid: order.uuid,
          futureOrderType: order.futureOrderType,
          orderProductQuantity: order.orderDetails.quantity,
          order,
        };
        if (!acc[materialData.materialId]) acc[materialData.materialId] = [];
        acc[materialData.materialId].push(futureOrder);
      }
    });
    return acc;
  }, {} as Record<string, FutureOrdersByMaterial[]>);

  return Object.entries(materialMap).map(([materialId, data]) => ({ materialId, data }));
};

interface Props {
  futureOrders: IFutureOrder[];
  currentMaterialId: string;
  setIsLoading: (_isLoading: boolean) => void;
  refetchOrders: () => void;
  isEditEnabled: boolean;
}

export const FutureOrderTableByMaterials: React.FC<Props> = ({
  futureOrders: allFutureOrders,
  currentMaterialId,
  setIsLoading,
  refetchOrders,
  isEditEnabled,
}) => {
  const [futureOrdersByMaterials, setFutureOrdersByMaterials] = useState<
    { data: FutureOrdersByMaterial[]; materialId: string }[]
  >([]);
  const { account } = useAccountStore();

  const { materialsMap } = useMaterialsStore();
  const { materialColorDictionary } = useAccountStore();
  const { setEditedFutureOrder } = useFutureOrderStore();
  const navigate = useNavigate();

  useEffect(() => {
    setFutureOrdersByMaterials(
      mapFutureOrdersByMaterial(allFutureOrders, currentMaterialId === 'general' ? undefined : [currentMaterialId]),
    );
  }, [allFutureOrders, currentMaterialId]);

  const columns: Column<{ data: FutureOrdersByMaterial[]; materialId: string }>[] = useMemo(
    () => [
      {
        Header: 'Material',
        accessor: (row) => row.materialId,
        Cell: ({ value }: CellProps<{ data: FutureOrdersByMaterial[]; materialId: string }>) => (
          <span>{`${materialsMap[value].name}`}</span>
        ),
      },
      {
        Header: 'Delivery Date(s)',
        accessor: (row) => row.data,
        Cell: ({
          value,
        }: CellProps<{ data: FutureOrdersByMaterial[]; materialId: string }, FutureOrdersByMaterial[]>) => {
          const datesList = value.flatMap((materialItem) =>
            materialItem.materialData.exposureCalendar.map((item) => item.date),
          );
          return <span>{getDatesRangeAsString(datesList)}</span>;
        },
      },
      {
        Header: 'Order ID',
        accessor: (row) => row.data,
        Cell: ({
          value,
        }: CellProps<{ data: FutureOrdersByMaterial[]; materialId: string }, FutureOrdersByMaterial[]>) => {
          return value.length > 1 ? (
            <span>Multi</span>
          ) : (
            <FutureOrderIdLink
              futureOrderType={value[0].futureOrderType}
              orderId={value[0].orderId}
              orderUuid={value[0].orderUuid}
            />
          );
        },
      },
      {
        Header: 'Quantity',
        accessor: (row) => row.data,
        Cell: ({
          value,
        }: CellProps<{ data: FutureOrdersByMaterial[]; materialId: string }, FutureOrdersByMaterial[]>) => {
          const totalQuantity = value.reduce((sum, order) => {
            return sum + order.materialData.weightInProduct * order.orderProductQuantity;
          }, 0);
          return (
            <span>
              {formatToShortString(totalQuantity, 0)}{' '}
              {getUnitObj(materialsMap[value[0].materialData.materialId]?.unit).shortForm}
            </span>
          ); // seems like quantity is irrelevant
        },
      },

      {
        Header: 'Total Price',
        accessor: (row) => row.data,
        Cell: ({
          value,
        }: CellProps<{ data: FutureOrdersByMaterial[]; materialId: string }, FutureOrdersByMaterial[]>) => {
          const totalPrice = value.reduce((sum, order) => {
            return sum + order.materialData.basePrice * order.materialData.weightInProduct * order.orderProductQuantity;
          }, 0);
          return <span>{FormatToShortPriceTemplate(totalPrice)}</span>;
        },
      },
      {
        Header: 'Adjustment',
        accessor: (row) => row.data,
        Cell: ({
          value,
          row,
        }: CellProps<{ data: FutureOrdersByMaterial[]; materialId: string }, FutureOrdersByMaterial[]>) => {
          const allAdjustments = value.map((order) => order.materialData.adjustment);
          const uniqueAdjustments = new Set(allAdjustments);
          return (
            <div className="flex justify-between ">
              <span>{uniqueAdjustments.size === 1 ? allAdjustments[0] : 'Multi'}</span>
              <div className="pe-[4rem] relative">
                <img
                  className={`${row.isExpanded ? '-rotate-90' : 'rotate-90'} transition-transform absolute`}
                  title="down"
                  src="../assets/icons/flat/chevron-right_purple.svg"
                  style={{ width: '34px', height: '34px' }}
                />
              </div>
            </div>
          );
        },
      },
    ],
    [materialsMap, futureOrdersByMaterials],
  );

  const tableInstance = useTable(
    {
      columns,
      data: futureOrdersByMaterials,
    },
    useFilters,
    useExpanded,
  );
  const { getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  const [confirmDelete, setConfirmDelete] = React.useState<FutureOrdersByMaterial | undefined>();
  const deleteOrder = async (order: FutureOrdersByMaterial) => {
    setIsLoading(true);
    setConfirmDelete(undefined);

    await deleteFutureOrder(order.orderUuid || '', account?.id);

    refetchOrders();
    setIsLoading(false);
  };

  const editOrder = async (order: FutureOrdersByMaterial) => {
    setIsLoading(true);
    await modifyCompletedOrder(
      order.order,
      account?.id || '',
      (futureOrder) => setEditedFutureOrder(futureOrder),
      navigate,
    );
    setIsLoading(false);
  };

  return (
    <div className="flex flex-1  overflow-y-auto ">
      <table className=" flex-1   overflow-scroll">
        <thead className=" mb-[1rem]">
          {headerGroups.map((headerGroup, index) => (
            <tr key={index} className="">
              {headerGroup.headers.map((column, idx) => (
                <th key={idx} className="bg-gray-50 text-secondary_text font-normal text-base py-[0.75rem] ps-[1rem]  ">
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className=" border-white ">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <React.Fragment key={row.id}>
                <tr
                  {...row.getRowProps()}
                  onClick={() => row.toggleRowExpanded()}
                  className={`bg-white `}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: row.isExpanded ? '#f5f5f5' : 'white',
                    borderTop: '6px solid #ffffff',
                    borderBottom: '6px solid #ffffff',
                  }}
                >
                  {row.cells.map((cell, index) => (
                    <td key={index} className=" font-medium  ">
                      <div
                        style={
                          index === 0
                            ? { borderLeft: `6px solid ${materialColorDictionary[row.original.materialId] || 'red'}` }
                            : index === row.cells.length - 1
                            ? { borderRight: '1px solid #f0f0f0' }
                            : {}
                        }
                        className="border-y   border-gray-100 m-0 py-[0.75rem] ps-[0.75rem] whitespace-nowrap overflow-hidden text-ellipsis"
                      >
                        {cell.render('Cell')}
                      </div>
                    </td>
                  ))}
                </tr>
                {row.isExpanded
                  ? row.original.data.map((order) => (
                      <tr key={order.orderId} className=" bg-gray-50 text-sm border-y border-y-white">
                        <td>
                          <div
                            className="flex h-full border-l-[0.4rem] "
                            style={{ borderLeftColor: materialColorDictionary[row.original.materialId] }}
                          >
                            <div className="ps-[0.7rem] py-[1rem]">{materialsMap[row.original.materialId].name}</div>
                          </div>
                        </td>
                        <td className="ps-[1rem] py-[1rem]">
                          {getDatesRangeAsString(order.materialData.exposureCalendar.map((item) => item.date))}
                        </td>
                        <td className="ps-[1rem] py-[1rem]">
                          <FutureOrderIdLink
                            futureOrderType={order.futureOrderType}
                            orderId={order.orderId}
                            orderUuid={order.orderUuid}
                            maxLength={6}
                          />
                        </td>
                        <td className="ps-[1rem] py-[1rem]">
                          {formatToShortString(order.orderProductQuantity * order.materialData.weightInProduct, 0)}
                          {getUnitObj(materialsMap[row.original.materialId]?.unit).shortForm}
                        </td>
                        <td className="ps-[1rem] py-[1rem]">
                          {FormatToShortPriceTemplate(
                            order.orderProductQuantity *
                              order.materialData.weightInProduct *
                              order.materialData.basePrice,
                          )}
                        </td>
                        <td className="">
                          <div className="flex justify-between items-center pe-3">
                            {order.materialData.adjustment}
                            {isEditEnabled && (
                              <div>
                                <Button onClick={() => editOrder(order)} className="text-blue-600 h-6 w-4 px-3 py-1">
                                  <Edit />
                                </Button>
                                <Button
                                  onClick={() => setConfirmDelete(order)}
                                  className="text-blue-600 h-6 w-4 px-3 py-1"
                                >
                                  <Delete />
                                </Button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  : null}
                {confirmDelete && (
                  <ConfirmDelete
                    onAction={() => deleteOrder(confirmDelete)}
                    onCancel={() => setConfirmDelete(undefined)}
                    title="Confirmation"
                    description={`Are you sure you want to delete order ${confirmDelete.orderId}?`}
                    actionText="Delete"
                  />
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
